import { useCallback, useEffect, useState } from 'react';
import { Container } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getCohortStatusColor,
  getCohortStatusLabel,
} from '../../../config/statusConfig';
import { useDocumentTitle } from '../../../hooks';
import { cohortsApi } from '../../../services/api/cohorts';
import { matchingSessionsApi } from '../../../services/api/matchingSessions';
import { MatchingWorkspace } from './MatchingWorkspace';
import { wsTransport } from './transport/wsTransport';

/** Router state passed by Set Up Matching, so the first connect skips a re-mint. */
export interface MatchingPageState {
  matchingUrl?: string;
}

/**
 * /cohorts/:cohortId/matching/:sessionId — the in-app matching tool.
 * Opens from Set Up Matching (right after the session is created) or from a
 * session row's Continue. Shows the setup flow or the progress screen.
 */
export function MatchingPage() {
  const { cohortId = '', sessionId = '' } = useParams<{
    cohortId: string;
    sessionId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  useDocumentTitle('Matching · Medley');

  // Read the one-time URL once, then drop it from history so a reload re-mints
  // instead of reusing a token that has already been spent.
  const [initialUrl] = useState(
    () => (location.state as MatchingPageState | null)?.matchingUrl ?? null
  );
  useEffect(() => {
    if (location.state)
      navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const { data: cohort } = useQuery({
    queryKey: ['cohort', cohortId],
    queryFn: () => cohortsApi.getById(cohortId),
    enabled: !!cohortId,
  });
  const { data: sessions } = useQuery({
    queryKey: ['matchingSessions', cohortId],
    queryFn: () => matchingSessionsApi.list(cohortId),
    enabled: !!cohortId,
  });

  const mintUrl = useCallback(
    () => matchingSessionsApi.resume(sessionId).then(r => r.matching_url),
    [sessionId]
  );

  // "Request N": this session's position among the cohort's sessions, oldest first.
  const ordered = [...(sessions ?? [])].sort((a, b) =>
    a.created_at.localeCompare(b.created_at)
  );
  const position = ordered.findIndex(s => s.id === sessionId);
  const requestNumber = position >= 0 ? position + 1 : ordered.length + 1;

  const [reviewing, setReviewing] = useState(false);
  const reviewResults = async () => {
    // Same new-tab pattern as Resume: open synchronously so popup blockers allow it.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    setReviewing(true);
    try {
      const { matching_url } = await matchingSessionsApi.resume(sessionId);
      if (tab && !tab.closed) tab.location.href = matching_url;
      else window.location.href = matching_url;
    } catch (err) {
      if (tab && !tab.closed) tab.close();
      notifications.show({
        color: 'red',
        title: "Couldn't open results",
        message:
          (err as { message?: string })?.message ?? 'Try again in a moment.',
      });
    } finally {
      setReviewing(false);
    }
  };

  const code = cohort?.code ?? cohort?.short_name;
  const meta = cohort
    ? [code, cohort.partner_name, `${cohort.participant_count} participants`]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Container size="xl" py="xl">
      <MatchingWorkspace
        cohortId={cohortId}
        cohortName={cohort?.name ?? 'Back to cohort'}
        meta={meta}
        statusLabel={cohort ? getCohortStatusLabel(cohort.status) : undefined}
        statusColor={cohort ? getCohortStatusColor(cohort.status) : undefined}
        transport={wsTransport}
        initialUrl={initialUrl}
        mintUrl={mintUrl}
        requestNumber={requestNumber}
        onReviewResults={reviewResults}
        reviewingResults={reviewing}
      />
    </Container>
  );
}
